import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useMemo, useState } from 'react';
import { Filters } from '../models/Filters';
import { useClients } from './useClients';
import { useStartImpersonation } from './useImpersonate';

export const useClientsMain = () => {
  const { params: queryParams, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    origins: { type: 'array', default: [] as string[] },
  });

  const searchTerm = queryParams.search ?? '';
  const customerOrigins = queryParams.origins ?? [];

  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    'name',
    'user.name',
    'user.email',
    'country',
    'city',
    'created_at',
  ]);

  const filters: Filters = useMemo(() => {
    const trimmed = searchTerm.trim();
    return {
      ...(trimmed && { name: trimmed }),
      ...(customerOrigins.length > 0 && { customer_origins: customerOrigins }),
      limit: 20,
    };
  }, [searchTerm, customerOrigins]);

  const { clients, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useClients(filters);

  const startImpersonation = useStartImpersonation();

  const handleSearch = useCallback(
    (value: string) => {
      updateParam('search', value.trim());
    },
    [updateParam],
  );

  const handleCustomerOriginsChange = useCallback(
    (origins: string[]) => {
      updateParam('origins', origins);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((newColumns: string[]) => {
    setSelectedColumns(newColumns);
  }, []);

  const handleImpersonate = useCallback(
    (userId: number) => {
      startImpersonation.mutate(userId);
    },
    [startImpersonation],
  );

  const hasActiveFilters = useMemo(
    () => searchTerm.trim().length > 0 || customerOrigins.length > 0,
    [searchTerm, customerOrigins],
  );

  const tableColumns = useMemo(
    () => [
      { id: 'name', accessorKey: 'name', header: 'Nombre' },
      { id: 'user.name', accessorKey: 'user.name', header: 'Usuario' },
      { id: 'user.email', accessorKey: 'user.email', header: 'Email' },
      { id: 'user.phone', accessorKey: 'user.phone', header: 'Teléfono' },
      { id: 'country', accessorKey: 'country', header: 'País' },
      { id: 'city', accessorKey: 'city', header: 'Ciudad' },
      { id: 'created_at', accessorKey: 'created_at', header: 'Fecha de creación' },
    ],
    [],
  );

  const clientsViewProps = useMemo(
    () => ({
      clients,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      selectedColumns,
      refetch,
      onImpersonate: handleImpersonate,
    }),
    [clients, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, selectedColumns, refetch, handleImpersonate],
  );

  return {
    selectedColumns,
    tableColumns,

    clients,
    isLoading,
    isFetching,
    error,
    total,

    clientsViewProps,

    searchTerm,
    customerOrigins,
    handleSearch,
    handleCustomerOriginsChange,
    handleColumnsChange,
    handleImpersonate,
    refetch,

    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    hasActiveFilters,
  };
};
